/**
 * Shared directory row shell — media + text + trailing / under-text actions.
 *
 * Picker surfaces (Obchody / Organizace): comfortable density + optional whole-row press.
 * Account lists: compact density.
 * RTL: thumb is first in DOM → logical start; titles use `text-start`.
 */

import type { CSSProperties, MouseEvent, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { DIRECTORY_PICKER_ROW_PAD_CLASS } from './directoryPickerLayout.js';
import {
  DirectoryEntityThumb,
  type DirectoryEntityThumbProps,
  type DirectoryEntityThumbSize,
} from './DirectoryEntityThumb.js';

export type DirectoryEntityRowAlign = 'start' | 'center';

/** `compact` = dense account lists; `comfortable` = Obchody / Organizace pickers. */
export type DirectoryEntityRowDensity = 'compact' | 'comfortable';

/** `inline` = thumb beside text; `stack` = thumb on top, copy + actions under. */
export type DirectoryEntityRowLayout = 'inline' | 'stack';

export interface DirectoryEntityRowProps {
  readonly thumb: DirectoryEntityThumbProps;
  readonly title: ReactNode;
  /**
   * Title-only button (legacy). Prefer `onRowPress` on Obchody / Oblíbené pickers
   * so the whole identity row opens the shop (Maps / Slack pattern).
   */
  readonly onTitleClick?: () => void;
  readonly titleDisabled?: boolean;
  readonly titleTestId?: string;
  /**
   * Whole-row primary press (thumb + title + chevron). When set, `onTitleClick`
   * is ignored so nested buttons are never rendered.
   */
  readonly onRowPress?: () => void;
  /** When set, whole-row press is a real link (right-click / new-tab friendly). */
  readonly rowPressHref?: string;
  readonly rowPressDisabled?: boolean;
  /** Test id on the row-press control (Obchody / Oblíbené open). */
  readonly rowPressTestId?: string;
  readonly subtitle?: ReactNode;
  /** Trailing cluster outside the row-press hit target (star / status). */
  readonly trailing?: ReactNode;
  /** Disclosure chevron — navigation affordance when `onRowPress` or `rowPressHref` is set. */
  readonly showChevron?: boolean;
  /**
   * Under-media actions when row press is not used.
   * Prefer one primary button + text links.
   */
  readonly actions?: ReactNode;
  /** Override comfortable `grid-cols-2` / compact flex-wrap actions layout. */
  readonly actionsClassName?: string;
  /** Optional inline style on the actions shell (e.g. Objednávky `--rp-order-action-count`). */
  readonly actionsInlineStyle?: CSSProperties;
  /**
   * `start` — rows with under-text actions.
   * `center` — single-line store / membership rows.
   */
  readonly align?: DirectoryEntityRowAlign;
  readonly density?: DirectoryEntityRowDensity;
  /** Default `inline` (Obchody / Organizace pickers). */
  readonly layout?: DirectoryEntityRowLayout;
  readonly className?: string;
  readonly testId?: string;
}

const TITLE_BUTTON_COMPACT =
  'block max-w-full text-start text-sm font-medium leading-snug text-[var(--color-on-surface)] break-words underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus-ring)] disabled:opacity-50';

const TITLE_BUTTON_COMFORTABLE =
  'block max-w-full text-start text-[0.9375rem] font-semibold leading-snug tracking-tight text-[var(--color-on-surface)] break-words underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus-ring)] disabled:opacity-50';

const TITLE_STATIC_COMPACT =
  'block max-w-full text-start text-sm font-medium leading-snug text-[var(--color-on-surface)] break-words';

const TITLE_STATIC_COMFORTABLE =
  'block max-w-full text-start text-[0.9375rem] font-semibold leading-snug tracking-tight text-[var(--color-on-surface)] break-words';

const ROW_PRESS_CLASS = [
  'flex h-full min-h-0 min-w-0 flex-1 gap-4 rounded-sm text-start no-underline text-inherit',
  '-mx-1 -my-0.5 px-1 py-0.5',
  'transition-colors duration-150',
  'hover:bg-[var(--color-surface-hover)] active:bg-[var(--color-surface-muted)]',
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
  'focus-visible:outline-[var(--color-focus-ring)]',
  'disabled:opacity-50 disabled:hover:bg-transparent',
  'motion-reduce:transition-none',
].join(' ');

/**
 * Karty stack shell — bleeds into DIRECTORY_CARD_PAD (`p-4`) so the stretch press
 * covers the whole elevated cell. Hover tint is on the `<li>` (DIRECTORY_CARD_ITEM_CLASS).
 * Disabled styling keys off the stretch press only (not the favorite star).
 */
const STACK_CARD_SHELL_CLASS = [
  'group relative flex h-full min-h-0 flex-col gap-4',
  '-m-4 rounded-lg p-4',
  'has-[[data-directory-row-press]:disabled]:opacity-50',
].join(' ');

/** Invisible stretched press target — covers the full Karty cell. */
const STACK_STRETCH_PRESS_CLASS = [
  'absolute inset-0 z-0 rounded-lg no-underline',
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
  'focus-visible:outline-[var(--color-focus-ring)]',
].join(' ');

function isModifiedNavigationClick(event: MouseEvent<HTMLAnchorElement>): boolean {
  return (
    event.metaKey
    || event.ctrlKey
    || event.shiftKey
    || event.altKey
    || event.button !== 0
  );
}

function handleRowPressLinkClick(
  event: MouseEvent<HTMLAnchorElement>,
  disabled: boolean,
  onRowPress?: () => void,
): void {
  if (disabled) {
    event.preventDefault();
    return;
  }
  if (!isModifiedNavigationClick(event)) {
    onRowPress?.();
  }
}

function DisclosureChevron(): JSX.Element {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden="true"
      className="h-5 w-5 shrink-0 self-center text-[var(--color-on-surface-muted)]"
    >
      {/* Short beak chevron (›) — thicker stroke, less “shooting arrow”. */}
      <path
        d="M8 5.25 13.25 10 8 14.75"
        stroke="currentColor"
        strokeWidth="2.35"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function TextColumn(props: {
  readonly title: ReactNode;
  readonly subtitle?: ReactNode;
  readonly titleTestId?: string;
  readonly titleId?: string;
  readonly onTitleClick?: () => void;
  readonly titleDisabled?: boolean;
  readonly comfortable: boolean;
  readonly actions?: ReactNode;
  readonly actionsClass: string;
  readonly subtitleClass: string;
}): JSX.Element {
  const titleButtonClass = props.comfortable ? TITLE_BUTTON_COMFORTABLE : TITLE_BUTTON_COMPACT;
  const titleStaticClass = props.comfortable ? TITLE_STATIC_COMFORTABLE : TITLE_STATIC_COMPACT;

  return (
    <div className="min-w-0 flex-1">
      {props.onTitleClick !== undefined ? (
        <button
          type="button"
          className={titleButtonClass}
          disabled={props.titleDisabled === true}
          data-testid={props.titleTestId}
          id={props.titleId}
          onClick={props.onTitleClick}
        >
          {props.title}
        </button>
      ) : (
        <p className={titleStaticClass} data-testid={props.titleTestId} id={props.titleId}>
          {props.title}
        </p>
      )}

      {props.subtitle !== undefined && props.subtitle !== null && props.subtitle !== false ? (
        <p className={props.subtitleClass}>{props.subtitle}</p>
      ) : null}

      {props.actions !== undefined && props.actions !== null ? (
        <div className={props.actionsClass}>{props.actions}</div>
      ) : null}
    </div>
  );
}

function StackMediaActions(props: {
  readonly trailing?: ReactNode;
  readonly showChevron: boolean;
  /** When true, only trailing controls receive clicks (chevron stays pass-through). */
  readonly interactiveTrailing: boolean;
}): JSX.Element | null {
  const hasTrailing = props.trailing !== undefined && props.trailing !== null;
  if (!hasTrailing && !props.showChevron) {
    return null;
  }

  return (
    <div className="relative z-[1] flex shrink-0 items-center gap-1.5">
      {hasTrailing ? (
        <div className={props.interactiveTrailing ? 'pointer-events-auto' : undefined}>
          {props.trailing}
        </div>
      ) : null}
      {props.showChevron ? <DisclosureChevron /> : null}
    </div>
  );
}

export function DirectoryEntityRow(props: DirectoryEntityRowProps): JSX.Element {
  const {
    thumb,
    title,
    onTitleClick,
    titleDisabled = false,
    titleTestId,
    onRowPress,
    rowPressHref,
    rowPressDisabled = false,
    rowPressTestId,
    subtitle,
    trailing,
    showChevron = false,
    actions,
    actionsClassName,
    actionsInlineStyle,
    align = actions !== undefined && actions !== null ? 'start' : 'center',
    density = 'compact',
    layout = 'inline',
    className,
    testId = 'directory-entity-row',
  } = props;

  const comfortable = density === 'comfortable';
  const alignClass = align === 'start' ? 'items-start' : 'items-center';
  /** Comfortable ≈14–16px; with 64px thumb + py-3 pad → ~96–104px row. */
  const gapClass = comfortable ? 'gap-4' : 'gap-3';
  /** Wrap (no single-line ellipsis) so shop/org copy stays readable in split cells. */
  const subtitleClass = comfortable
    ? 'mt-0.5 text-start text-[0.8125rem] leading-snug text-[var(--color-on-surface-muted)] break-words'
    : 'text-start text-xs leading-snug text-[var(--color-on-surface-muted)] break-words';
  const actionsClass =
    actionsClassName ??
    (comfortable
      ? 'grid grid-cols-2 gap-2'
      : 'mt-2 flex flex-wrap gap-2');

  if (layout === 'stack') {
    if (rowPressHref !== undefined || onRowPress !== undefined) {
      const titleDomId = `${testId}-title`;
      const pressLabel = typeof title === 'string' ? title : undefined;
      const rowPressControl =
        rowPressHref !== undefined ? (
          <Link
            to={rowPressHref}
            className={[
              STACK_STRETCH_PRESS_CLASS,
              rowPressDisabled ? 'pointer-events-none' : undefined,
            ]
              .filter(Boolean)
              .join(' ')}
            aria-disabled={rowPressDisabled ? true : undefined}
            tabIndex={rowPressDisabled ? -1 : undefined}
            data-directory-row-press=""
            data-testid={rowPressTestId ?? titleTestId}
            aria-labelledby={pressLabel === undefined ? titleDomId : undefined}
            aria-label={pressLabel}
            onClick={(event) => {
              handleRowPressLinkClick(event, rowPressDisabled, onRowPress);
            }}
          />
        ) : (
          <button
            type="button"
            className={STACK_STRETCH_PRESS_CLASS}
            disabled={rowPressDisabled}
            data-directory-row-press=""
            data-testid={rowPressTestId ?? titleTestId}
            aria-labelledby={pressLabel === undefined ? titleDomId : undefined}
            aria-label={pressLabel}
            onClick={onRowPress}
          />
        );

      return (
        <div
          className={[STACK_CARD_SHELL_CLASS, className].filter(Boolean).join(' ')}
          data-testid={testId}
          data-align="start"
          data-density={density}
          data-layout="stack"
          data-row-press="true"
        >
          {rowPressControl}
          {/*
            Content above the stretch press. Media + copy are non-interactive
            (press goes through to the absolute button). Star/chevron keep clicks.
          */}
          <div className="relative z-[1] flex w-full min-w-0 items-start justify-between gap-3 pointer-events-none">
            <DirectoryEntityThumb {...thumb} size="xl" />
            <StackMediaActions
              trailing={trailing}
              showChevron={showChevron}
              interactiveTrailing
            />
          </div>
          <div className="relative z-[1] min-w-0 pointer-events-none">
            <TextColumn
              title={title}
              subtitle={subtitle}
              titleId={pressLabel === undefined ? titleDomId : undefined}
              comfortable={comfortable}
              subtitleClass={subtitleClass}
              actionsClass={actionsClass}
            />
          </div>
          {actions !== undefined && actions !== null ? (
            <div className={['relative z-[1] pointer-events-auto', actionsClass].join(' ')}>
              {actions}
            </div>
          ) : null}
        </div>
      );
    }

    return (
      <div
        className={['group flex flex-col gap-4', className].filter(Boolean).join(' ')}
        data-testid={testId}
        data-align="start"
        data-density={density}
        data-layout="stack"
      >
        <div className="flex w-full min-w-0 items-start justify-between gap-3">
          <DirectoryEntityThumb {...thumb} size="xl" />
          <StackMediaActions
            trailing={trailing}
            showChevron={showChevron}
            interactiveTrailing={false}
          />
        </div>
        <TextColumn
          title={title}
          subtitle={subtitle}
          titleTestId={titleTestId}
          onTitleClick={onTitleClick}
          titleDisabled={titleDisabled}
          comfortable={comfortable}
          actions={actions}
          subtitleClass={subtitleClass}
          actionsClass={actionsClass}
        />
      </div>
    );
  }

  if (rowPressHref !== undefined || onRowPress !== undefined) {
    const rowPressControl =
      rowPressHref !== undefined ? (
        <Link
          to={rowPressHref}
          className={[
            ROW_PRESS_CLASS,
            comfortable ? 'items-start' : 'items-center',
            rowPressDisabled ? 'pointer-events-none' : undefined,
          ]
            .filter(Boolean)
            .join(' ')}
          aria-disabled={rowPressDisabled ? true : undefined}
          tabIndex={rowPressDisabled ? -1 : undefined}
          data-testid={rowPressTestId ?? titleTestId}
          onClick={(event) => {
            handleRowPressLinkClick(event, rowPressDisabled, onRowPress);
          }}
        >
          <DirectoryEntityThumb {...thumb} />
          <TextColumn
            title={title}
            subtitle={subtitle}
            comfortable={comfortable}
            subtitleClass={subtitleClass}
            actionsClass={actionsClass}
          />
          {showChevron ? <DisclosureChevron /> : null}
        </Link>
      ) : (
        <button
          type="button"
          className={[
            ROW_PRESS_CLASS,
            comfortable ? 'items-start' : 'items-center',
          ].join(' ')}
          disabled={rowPressDisabled}
          data-testid={rowPressTestId ?? titleTestId}
          onClick={onRowPress}
        >
          <DirectoryEntityThumb {...thumb} />
          <TextColumn
            title={title}
            subtitle={subtitle}
            comfortable={comfortable}
            subtitleClass={subtitleClass}
            actionsClass={actionsClass}
          />
          {showChevron ? <DisclosureChevron /> : null}
        </button>
      );

    return (
      <div
        className={[
          'group flex h-full w-full min-h-0',
          comfortable ? 'items-start' : 'items-center',
          gapClass,
          className,
        ]
          .filter(Boolean)
          .join(' ')}
        data-testid={testId}
        data-align={comfortable ? 'start' : 'center'}
        data-density={density}
        data-layout="inline"
        data-row-press="true"
      >
        {rowPressControl}
        {trailing !== undefined && trailing !== null ? (
          <div className="relative z-[1] flex shrink-0 items-center gap-1.5 self-center">
            {trailing}
          </div>
        ) : null}
      </div>
    );
  }

  /**
   * Identity row (thumb + title + trailing); optional full-width actions under
   * the media row. Trailing stays on the identity row even when `actions` is set
   * (price / status right of title — not below CTAs).
   */
  const hasActions = actions !== undefined && actions !== null;
  const hasTrailing = trailing !== undefined && trailing !== null;
  const identityAlignClass =
    comfortable || align === 'start' ? 'items-start' : 'items-center';

  return (
    <div
      className={[
        'group flex',
        hasActions ? 'flex-col gap-3' : gapClass,
        hasActions ? '' : alignClass,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      data-testid={testId}
      data-align={hasActions ? 'start' : align}
      data-density={density}
      data-layout="inline"
    >
      <div className={['flex w-full min-w-0', identityAlignClass, gapClass].join(' ')}>
        <DirectoryEntityThumb {...thumb} />
        <TextColumn
          title={title}
          subtitle={subtitle}
          titleTestId={titleTestId}
          onTitleClick={onTitleClick}
          titleDisabled={titleDisabled}
          comfortable={comfortable}
          actionsClass={actionsClass}
          subtitleClass={subtitleClass}
        />
        {hasTrailing ? (
          <div className="ml-auto flex shrink-0 flex-col items-end gap-1 self-start">
            {trailing}
          </div>
        ) : null}
      </div>
      {hasActions ? (
        <div className={actionsClass} style={actionsInlineStyle}>
          {actions}
        </div>
      ) : null}
    </div>
  );
}

export interface DirectoryEntityRowSkeletonProps {
  readonly size?: DirectoryEntityThumbSize;
  readonly density?: DirectoryEntityRowDensity;
  readonly className?: string;
  readonly testId?: string;
}

/**
 * Loading placeholder — media slot + two text lines (A8).
 * Size follows thumb tokens (md 48 / lg 64 / xl 80).
 */
export function DirectoryEntityRowSkeleton(
  props: DirectoryEntityRowSkeletonProps = {},
): JSX.Element {
  const {
    size = 'md',
    density = 'compact',
    className,
    testId = 'directory-entity-row-skeleton',
  } = props;
  const comfortable = density === 'comfortable';
  let mediaSizeClass =
    'h-[var(--directory-thumb-size)] w-[var(--directory-thumb-size)] rounded-[var(--directory-thumb-radius)]';
  if (size === 'xl') {
    mediaSizeClass =
      'h-[var(--directory-thumb-size-xl)] w-[var(--directory-thumb-size-xl)] rounded-[var(--directory-thumb-radius-xl)]';
  } else if (size === 'lg') {
    mediaSizeClass =
      'h-[var(--directory-thumb-size-lg)] w-[var(--directory-thumb-size-lg)] rounded-[var(--directory-thumb-radius-lg)]';
  }

  return (
    <div
      className={[
        'flex',
        // Comfortable list rows use items-start + taller copy; match to limit CLS (§4).
        comfortable ? 'items-start gap-4' : 'items-center gap-3',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      aria-busy="true"
      aria-live="polite"
      data-testid={testId}
      data-size={size}
      data-density={density}
    >
      <span
        className={[
          'inline-block shrink-0 animate-pulse',
          mediaSizeClass,
          'bg-[var(--directory-thumb-skeleton)]',
          'motion-reduce:animate-none',
        ].join(' ')}
        data-testid={`${testId}-media`}
      />
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <span
          className={[
            'animate-pulse rounded-md bg-[var(--directory-thumb-skeleton)] motion-reduce:animate-none',
            // Reserve ~2 title lines (comfortable) so loaded title/subtitle do not jump.
            comfortable ? 'h-8 w-3/4 max-w-[14rem]' : 'h-3 w-2/3 max-w-[12rem]',
          ].join(' ')}
          data-testid={`${testId}-line-1`}
        />
        <span
          className={[
            'animate-pulse rounded-md bg-[var(--directory-thumb-skeleton)] motion-reduce:animate-none',
            comfortable ? 'h-3 w-1/2 max-w-[10rem]' : 'h-3 w-1/2 max-w-[9rem]',
          ].join(' ')}
          data-testid={`${testId}-line-2`}
        />
      </div>
      {comfortable ? (
        <span
          className="mt-1 inline-block h-3.5 w-12 shrink-0 animate-pulse rounded-md bg-[var(--directory-thumb-skeleton)] motion-reduce:animate-none"
          data-testid={`${testId}-trailing`}
        />
      ) : null}
    </div>
  );
}

export interface DirectoryEntityRowSkeletonListProps {
  readonly count?: number;
  readonly size?: DirectoryEntityThumbSize;
  readonly density?: DirectoryEntityRowDensity;
  /** When true, render as divided group rows (no inter-item gap). */
  readonly grouped?: boolean;
  readonly className?: string;
  readonly testId?: string;
}

const SKELETON_KEYS = ['sk-dir-1', 'sk-dir-2', 'sk-dir-3', 'sk-dir-4', 'sk-dir-5'] as const;

export function DirectoryEntityRowSkeletonList(
  props: DirectoryEntityRowSkeletonListProps = {},
): JSX.Element {
  const {
    count = 3,
    size = 'md',
    density = 'compact',
    grouped = false,
    className,
    testId = 'directory-entity-row-skeleton-list',
  } = props;
  const keys = SKELETON_KEYS.slice(0, Math.min(Math.max(count, 1), SKELETON_KEYS.length));

  if (grouped) {
    return (
      <>
        {keys.map((key) => (
          <li key={key} className={DIRECTORY_PICKER_ROW_PAD_CLASS} data-testid={`${testId}-${key}`}>
            <DirectoryEntityRowSkeleton
              size={size}
              density={density}
              testId={`${testId}-${key}-row`}
            />
          </li>
        ))}
      </>
    );
  }

  const listGap = density === 'comfortable' ? 'gap-5' : 'gap-3';

  return (
    <ul
      className={['flex flex-col', listGap, className].filter(Boolean).join(' ')}
      data-testid={testId}
    >
      {keys.map((key) => (
        <li key={key}>
          <DirectoryEntityRowSkeleton
            size={size}
            density={density}
            testId={`${testId}-${key}`}
          />
        </li>
      ))}
    </ul>
  );
}
